import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Question, TriggerRule, TriggerCondition, PostVariableMapping } from "@/types/theme";
import { Json } from "@/integrations/supabase/types";
import { transformThemeData } from "@/utils/themeUtils";

// Define the type for the trigger rule data from the database
interface TriggerRuleData {
  id: string;
  chatbot_id: string;
  name: string;
  notes?: string;
  conditions: Json;
  webhookurl?: string;
  postvariablemappings?: Json; // Match database column name
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Define the type for chatbot data including custom code fields
interface ChatbotData {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  welcome_message: string;
  completion_message: string;
  title: string;
  avatar_url: string | null;
  operator_name: string | null;
  operator_avatar_url: string | null;
  company_name: string | null;
  company_logo: string | null;
  settings: Json | null;
  active: boolean | null;
  created_at: string;
  updated_at: string;
  custom_javascript: string | null;
  custom_html: string | null;
}

const isValidUUID = (uuid: string) => {
  // More permissive UUID regex that correctly matches standard UUIDs
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export const useChatbotQueries = (id: string | undefined) => {
  // Strict validation of chatbot ID - no fallback to prevent data leakage between bots
  if (id && !isValidUUID(id)) {
    console.error("Invalid chatbot ID format provided:", id);
  }

  // Only use the ID if it's valid, otherwise use undefined to ensure queries don't run
  const chatbotId = id && isValidUUID(id) ? id : undefined;

  const { data: chatbot, isLoading: isLoadingChatbot, error: chatbotError } = useQuery({
    queryKey: ["chatbot", chatbotId],
    queryFn: async () => {
      if (!chatbotId) {
        throw new Error("Invalid chatbot ID format");
      }

      // console.log("Fetching chatbot with ID:", chatbotId); // Reduced logging
      const { data, error } = await supabase
        .from("chatbots")
        .select("*")
        .eq("id", chatbotId)
        .single();

      if (error) {
        console.error("Error fetching chatbot:", error);
        if (error.code === 'PGRST116') {
          throw new Error("Chatbot not found");
        }
        throw new Error(`Failed to load chatbot: ${error.message}`);
      }

      if (!data) {
        throw new Error("Chatbot not found");
      }

      // console.log("Fetched chatbot:", data); // Reduced logging
      return data as ChatbotData;
    },
    enabled: !!chatbotId,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent excessive refetching
    retry: (failureCount, error) => {
      // Don't retry if chatbot not found
      if (error.message.includes('not found')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });

  const { data: theme, isLoading: isLoadingTheme, error: themeError } = useQuery({
    queryKey: ["theme", chatbotId],
    queryFn: async () => {
      if (!chatbotId) {
        throw new Error("Invalid chatbot ID format");
      }

      const { data, error } = await supabase
        .from("themes")
        .select("*")
        .eq("chatbot_id", chatbotId)
        .single();

      if (error) throw error;
      return transformThemeData(data);
    },
    enabled: !!chatbotId && !!chatbot,
  });

  const { data: questions, isLoading: isLoadingQuestions, error: questionsError } = useQuery({
    queryKey: ["questions", chatbotId],
    queryFn: async () => {
      if (!chatbotId) {
        throw new Error("Invalid chatbot ID format");
      }

      const { data, error } = await supabase
        .from("questions")
        .select("*")
        .eq("chatbot_id", chatbotId)
        .order("order", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!chatbotId && !!chatbot,
  });

  const { data: triggerRulesData, isLoading: isLoadingTriggers, error: triggerRulesError } = useQuery({
    queryKey: ["trigger_rules", chatbotId],
    queryFn: async () => {
      if (!chatbotId) return [];

      // console.log(" TRIGGER FETCH DEBUG: Starting fetch for chatbot:", chatbotId); // Reduced logging

      const { data, error } = await supabase
        .from("trigger_rules")
        .select("*")
        .eq("chatbot_id", chatbotId);

      // console.log(" TRIGGER FETCH DEBUG: Raw query result", { data, error }); // Reduced logging

      if (error) {
        console.error(" TRIGGER FETCH ERROR:", error);
        throw error;
      }

      // console.log(" TRIGGER FETCH DEBUG: Successfully fetched trigger rules:", data?.length || 0, "rules"); // Reduced logging
      return data as TriggerRuleData[] || [];
    },
    enabled: !!chatbotId,
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent excessive refetching
  });

  const transformedTriggerRules: TriggerRule[] = useMemo(() => {
    if (!triggerRulesData) return [];

    return triggerRulesData.map(rule => {
      let conditions: TriggerCondition[] = [];

      if (rule.conditions) {
        let parsedConditions: any[] = [];
        if (typeof rule.conditions === 'string') {
          try {
            parsedConditions = JSON.parse(rule.conditions);
          } catch (e) {
            console.error("Failed to parse conditions string:", e, rule.conditions);
          }
        } else if (Array.isArray(rule.conditions)) {
          parsedConditions = rule.conditions;
        }

        if (Array.isArray(parsedConditions)) {
          conditions = parsedConditions.map((condition: any) => ({
            questionId: condition.questionId || '',
            operator: condition.operator || 'equals',
            value: typeof condition.value === 'string' ? condition.value : String(condition.value ?? '')
          }));
        }
      }

      let postVariableMappings: PostVariableMapping[] = [];

      if (rule.postvariablemappings) {
        let parsedMappings: any[] = [];
        if (typeof rule.postvariablemappings === 'string') {
          try {
            parsedMappings = JSON.parse(rule.postvariablemappings);
          } catch (e) {
            console.error("Failed to parse postvariablemappings string:", e, rule.postvariablemappings);
          }
        } else if (Array.isArray(rule.postvariablemappings)) {
          parsedMappings = rule.postvariablemappings;
        }

        if (Array.isArray(parsedMappings)) {
          postVariableMappings = parsedMappings.map((mapping: any) => ({
            questionId: mapping.questionId || '',
            variableName: mapping.variableName || '',
            providerId: mapping.providerId
          }));
        }
      }

      return {
        id: rule.id,
        chatbot_id: rule.chatbot_id,
        name: rule.name,
        notes: rule.notes || '',
        conditions: conditions,
        webhookUrl: rule.webhookurl || '',
        postVariableMappings: postVariableMappings,
        active: rule.active !== false,
        created_at: rule.created_at,
        updated_at: rule.updated_at
      };
    });
  }, [triggerRulesData]);

  const { data: conversations, isLoading: isLoadingConversations } = useQuery({
    queryKey: ["conversations", chatbotId],
    queryFn: async () => {
      if (!chatbotId) {
        throw new Error("Invalid chatbot ID format");
      }

      const { data, error } = await supabase
        .from("conversations")
        .select("*")
        .eq("chatbot_id", chatbotId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!chatbotId && !!chatbot,
  });

  const transformedQuestions: Question[] = useMemo(() => {
    if (!questions) return [];

    return questions.map(q => {
      // Parse options properly
      let parsedOptions = null;
      if (q.options) {
        try {
          // If options is a string, try to parse it as JSON
          if (typeof q.options === 'string') {
            parsedOptions = JSON.parse(q.options);
          }
          // If options is already an array, use it directly
          else if (Array.isArray(q.options)) {
            parsedOptions = q.options;
          }

          // Ensure options have the correct format (array of {label, value} objects)
          if (Array.isArray(parsedOptions)) {
            // If it's an array of strings, convert to {label, value} format
            if (parsedOptions.length > 0 && typeof parsedOptions[0] === 'string') {
              parsedOptions = parsedOptions.map(opt => ({
                label: opt,
                value: opt
              }));
            }
          }
        } catch (e) {
          console.error('Error parsing question options:', e);
          parsedOptions = null;
        }
      }

      // Parse validation settings from responsevalidation field
      let validationRules = {};
      let validationType = 'none';

      // First try to get validation settings from validation_rules if it exists
      if (q.validation_rules) {
        try {
          if (typeof q.validation_rules === 'string') {
            validationRules = JSON.parse(q.validation_rules);
          } else {
            validationRules = q.validation_rules;
          }
        } catch (e) {
          console.error('Error parsing validation_rules:', e);
        }
      }

      // Then try to get validation settings from responsevalidation field
      if (q.responsevalidation) {
        try {
          const validationSettings = typeof q.responsevalidation === 'string'
            ? JSON.parse(q.responsevalidation || '{}')
            : (q.responsevalidation || {});

          // Extract validationType if it exists
          if (validationSettings.validationType) {
            validationType = validationSettings.validationType;
          }

          // Extract validation_rules if they exist
          if (validationSettings.validation_rules) {
            validationRules = {
              ...validationRules,
              ...validationSettings.validation_rules
            };
          }
        } catch (e) {
          console.error('Error parsing responsevalidation:', e);
        }
      }

      // Parse rich_content if it exists
      let richContent = null;
      if (q.rich_content) {
        try {
          if (typeof q.rich_content === 'string') {
            richContent = JSON.parse(q.rich_content);
          } else {
            richContent = q.rich_content;
          }
        } catch (e) {
          console.error('Error parsing rich_content:', e);
          richContent = null;
        }
      }

      const transformedQuestion = {
        id: q.id,
        chatbot_id: q.chatbot_id,
        text: q.text,
        type: q.type as 'text' | 'number' | 'select' | 'boolean' | 'button' | 'no_response',
        options: parsedOptions,
        order: q.order,
        shortname: q.shortname,
        errormessage: q.errormessage,
        responsevalidation: q.responsevalidation,
        validation_rules: validationRules,
        validationType: q.validationType || validationType || 'none',
        created_at: q.created_at,
        updated_at: q.updated_at,
        placeholder: q.placeholder,
        rich_content: richContent,
        enable_ai_response: q.enable_ai_response || false,
        ai_response_prompt: q.ai_response_prompt || null
      };

      // Log AI response settings for debugging
      if (q.enable_ai_response) {
        console.log('🤖 Question with AI response enabled:', {
          id: q.id,
          text: q.text.substring(0, 50) + '...',
          enable_ai_response: q.enable_ai_response,
          ai_response_prompt: q.ai_response_prompt
        });
      }

      return transformedQuestion;
    });
  }, [questions]);

  return {
    chatbot,
    theme,
    transformedQuestions,
    transformedTriggerRules,
    conversations,
    isLoading: isLoadingChatbot || isLoadingTheme || isLoadingQuestions || isLoadingTriggers || isLoadingConversations,
    error: chatbotError || themeError || questionsError || triggerRulesError,
  };
};

// Helper function to transform trigger rules
function transformTriggerRules(triggerRulesData: TriggerRuleData[]): TriggerRule[] {
  return triggerRulesData.map(rule => {
    let conditions: TriggerCondition[] = [];

    if (rule.conditions) {
      let parsedConditions: any[] = [];
      if (typeof rule.conditions === 'string') {
        try {
          parsedConditions = JSON.parse(rule.conditions);
        } catch (e) {
          console.error("Failed to parse conditions string:", e, rule.conditions);
        }
      } else if (Array.isArray(rule.conditions)) {
        parsedConditions = rule.conditions;
      }

      if (Array.isArray(parsedConditions)) {
        conditions = parsedConditions.map((condition: any) => ({
          questionId: condition.questionId || '',
          operator: condition.operator || 'equals',
          value: typeof condition.value === 'string' ? condition.value : String(condition.value ?? '')
        }));
      }
    }

    let postVariableMappings: PostVariableMapping[] = [];

    if (rule.postvariablemappings) {
      let parsedMappings: any[] = [];
      if (typeof rule.postvariablemappings === 'string') {
        try {
          parsedMappings = JSON.parse(rule.postvariablemappings);
        } catch (e) {
          console.error("Failed to parse postvariablemappings string:", e, rule.postvariablemappings);
        }
      } else if (Array.isArray(rule.postvariablemappings)) {
        parsedMappings = rule.postvariablemappings;
      }

      if (Array.isArray(parsedMappings)) {
        postVariableMappings = parsedMappings.map((mapping: any) => ({
          questionId: mapping.questionId || '',
          variableName: mapping.variableName || '',
          providerId: mapping.providerId
        }));
      }
    }

    return {
      id: rule.id,
      chatbot_id: rule.chatbot_id,
      name: rule.name,
      notes: rule.notes || '',
      conditions: conditions,
      webhookUrl: rule.webhookurl || '',
      postVariableMappings: postVariableMappings,
      active: rule.active !== false,
      created_at: rule.created_at,
      updated_at: rule.updated_at
    };
  });
}
